"use strict";

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

/**
 * SVG Object Storage Manager (SVGOSM) attempts to reduce the SVG DOM footprint
 * by moving all SVG objects into symbols so they can be re-used without having
 * to inject the full SVG structure in multiple places.
 *
 * @class
 * @param {String} name Name, id of the container to inject.
 * @public
 */
class SVGOSM {
  constructor(name) {
    this.id = name; // Name of the container.

    this.svg = null; // Reference to the svg container

    this.tmp = null; // Temp element we can abuse.

    this.tracker = {}; // Storage tracker to reduce DOM lookup.

    this.container = null; // Reference to the root container.

    this.mount(name);
  }
  /**
   * Inject a new SVG element that is going to be used as storage for all
   * our new symbols so they can be referenced in our HTML.
   *
   * @param {String} name The id of the container.
   * @returns {SVGElement|Null} The container if it was created.
   * @private
   */


  mount(name) {
    if (typeof document === 'undefined') return null;
    const container = document.createElement('div');
    container.style = 'display:none';
    container.id = name;
    const svg = document.createElementNS(SVGOSM.xmlns, 'svg');
    const tmp = document.createElement('div');
    container.appendChild(tmp);
    container.appendChild(svg); //
    // We don't know WHEN this script is being executed, it could be in the
    // head or body of a page, so we need to figure out the most ideal location
    // for the svg container to be injected in.
    //

    (document.body || document.documentElement).appendChild(container);
    this.container = container;
    this.tmp = tmp;
    this.svg = svg;
  }
  /**
   * Access our tmp node
   *
   * @param {String} className The className that needs to be assigned.
   * @returns {DOMElement} The updated DOM element.
   * @public
   */


  node(className) {
    if (!this.tmp) return null;
    this.tmp.className = className;
    return this.tmp;
  }
  /**
   * Extract information out the CSS of a given HTML element and extract the svg
   * content and the size of the element.
   *
   * @param {DOMElement} node A HTML element that we want to extract from.
   * @returns {Object} The resulting information.
   * @public
   */


  extract(node) {
    const style = window.getComputedStyle(node, ':before');
    const content = style.getPropertyValue('content');
    const height = style.getPropertyValue('height');
    const width = style.getPropertyValue('width');
    if (!content) return {};
    return _objectSpread({}, this.parse(content.replace(/data:image\/svg\+xml;utf-8,/, '').slice(5, -2)), {
      height,
      width
    });
  }
  /**
   * Parses the given SVG string and extracts contents.
   *
   * @param {String} str The encoded SVG string.
   * @returns {Object} Parsed SVG result.
   * @private
   */


  parse(str) {
    if (!~str.indexOf('svg')) return {};
    str = str.replace(/%23/g, '#');
    const index = str.indexOf('>');
    const viewBox = /viewBox=['"]([^']+?)['"]/.exec(str.substr(0, index))[1];
    const shapes = str.slice(index + 1, -6);
    return {
      shapes,
      viewBox
    };
  }
  /**
   * Returns the correct reference to the object.
   *
   * @param {String} name The name of the object we want to use.
   * @param {Function} [fn] Sets the value if there isn't any.
   * @returns {Object|Null} The value that should be used in the `xlink:href` attr.
   * @public
   */


  get(name, fn) {
    const res = this.tracker[name];
    if (res || !fn) return res;
    this.set(name, fn(name));
    return this.tracker[name];
  }
  /**
   * Store a new Symbol in our managed SVG object.
   *
   * @param {String} name Name of the symbol.
   * @param {Object} data Detailed information about the symbol.
   * @param {String} data.viewBox The viewBox of the symbol.
   * @param {String} data.title Title of the symbol.
   * @param {String} data.desc Description of the symbol.
   * @param {String} data.shapes The actual symbol that need to be stored.
   * @returns {Boolean} Indication if the shape was inserted.
   * @public
   */


  set(name, _ref = {}) {
    let {
      viewBox,
      title,
      desc,
      shapes
    } = _ref,
        data = _objectWithoutProperties(_ref, ["viewBox", "title", "desc", "shapes"]);

    if (!this.svg || !shapes) return false;
    const a11ytitle = `<title>${title || name}</title>`;
    const a11ydesc = desc ? `<desc>${desc}</desc>` : '';
    const sym = `<symbol id="${this.id}-${name}" viewBox="${viewBox}">${a11ytitle}${a11ydesc}${shapes}</symbol>`;
    this.tracker[name] = _objectSpread({}, data, {
      viewBox,
      link: `#${this.id}-${name}`
    }); //
    // REE PeRfOrMaNcE sUx fOr innerHTML: We don't care, this operation only
    // happens once per used icon, and parsing a potential DOM tree costs more
    // in terms in CPU and bundle size than any PeRfOrMaNcE hit we'll take by
    // doing innerHTML's
    //

    this.svg.innerHTML += sym;
    return true;
  }

}
/**
 * The XML namespace.
 *
 * @type {String}
 * @private
 */


SVGOSM.xmlns = 'http://www.w3.org/2000/svg'; //
// Expose the interface.
//

module.exports = SVGOSM;